import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Container,
  Grid,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardMedia,
  Slider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Upload as UploadIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  FindReplace as FindReplaceIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';

const CAROUSEL_POSITIONS = [
  { value: 1, label: 'Position 1 - Main Product Image' },
  { value: 2, label: 'Position 2 - Detailed Product View 1' },
  { value: 3, label: 'Position 3 - Detailed Product View 2' },
  { value: 4, label: 'Position 4 - Detailed Product View 3' },
  { value: 5, label: 'Position 5 - Detailed Product View 4' },
  { value: 6, label: 'Position 6 - Detailed Product View 5' }
];

function CarouselBatch() {
  const { user } = useAuth();

  // Mode state
  const [uploadMode, setUploadMode] = useState('packshots'); // 'packshots' or 'custom'

  // Common state
  const [gtinInput, setGtinInput] = useState('');
  const [gtinList, setGtinList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [recipientGuid, setRecipientGuid] = useState('');

  // Custom mode state
  const [imageSource, setImageSource] = useState('file'); // 'file' or 'url'
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [carouselPosition, setCarouselPosition] = useState(6);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Find & Replace mode state
  const [oldImageFile, setOldImageFile] = useState(null);
  const [oldImagePreview, setOldImagePreview] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [similarityThreshold, setSimilarityThreshold] = useState(10);
  const [scanResults, setScanResults] = useState(null);
  const [selectedMatches, setSelectedMatches] = useState({});  // {index: true/false}
  const [replacementHistory, setReplacementHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [scanning, setScanning] = useState(false);

  const fileInputRef = useRef(null);
  const oldImageInputRef = useRef(null);
  const newImageInputRef = useRef(null);

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setUploadMode(newMode);
      setError('');
      setResults(null);
      setScanResults(null);
    }
  };

  const handleImageSourceChange = (event, newSource) => {
    if (newSource !== null) {
      setImageSource(newSource);
      // Clear image when switching sources
      setImageFile(null);
      setImageUrl('');
      setImagePreview(null);
      setError('');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleOldImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      setOldImageFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setOldImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
      setScanResults(null);
    }
  };

  const handleNewImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      setNewImageFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setNewImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleUrlChange = (event) => {
    setImageUrl(event.target.value);
    setImagePreview(null);
  };

  const loadImageFromUrl = async () => {
    if (!imageUrl.trim()) {
      setError('Please enter an image URL');
      return;
    }

    setLoadingPreview(true);
    setError('');

    try {
      // Try to load the image to validate it
      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error('Failed to load image from URL'));
        img.src = imageUrl;
      });

      setImagePreview(imageUrl);
    } catch (err) {
      setError('Failed to load image from URL. Please check the URL and try again.');
      setImagePreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleInputChange = (e) => {
    setGtinInput(e.target.value);
  };

  const handleRecipientChange = (e) => {
    setRecipientGuid(e.target.value);
  };

  const processGtinInput = () => {
    // Split by newlines, commas, or spaces and filter out empty strings
    const gtins = gtinInput
      .split(/[\n,\s]+/)
      .map(g => g.trim())
      .filter(g => g.length > 0)
      // Remove duplicates
      .filter((value, index, self) => self.indexOf(value) === index);

    setGtinList(gtins);
    setError('');
    setResults(null);

    if (gtins.length === 0) {
      setError('Please enter at least one GTIN');
    } else if (gtins.length > 100) {
      setError('Maximum 100 GTINs per batch. You have ' + gtins.length);
    }
  };

  const clearAll = () => {
    setGtinInput('');
    setGtinList([]);
    setResults(null);
    setError('');
    setImageFile(null);
    setImageUrl('');
    setImagePreview(null);
    setOldImageFile(null);
    setOldImagePreview(null);
    setNewImageFile(null);
    setNewImagePreview(null);
    setScanResults(null);
    setSelectedMatches({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (oldImageInputRef.current) {
      oldImageInputRef.current.value = '';
    }
    if (newImageInputRef.current) {
      newImageInputRef.current.value = '';
    }
  };

  // Fetch replacement history
  const [historyTotal, setHistoryTotal] = useState(0);
  const fetchReplacementHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await apiClient.get('/carousel-batch/replacement-history?limit=200');
      setReplacementHistory(response.data.items || []);
      setHistoryTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch replacement history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load history when entering findreplace mode
  React.useEffect(() => {
    if (uploadMode === 'findreplace') {
      fetchReplacementHistory();
    }
  }, [uploadMode]);

  const uploadToCarousel6 = async () => {
    if (gtinList.length === 0) {
      setError('Please enter and process GTINs first');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const payload = {
        gtins: gtinList
      };

      if (recipientGuid) {
        payload.recipient_guid = recipientGuid;
      }

      const response = await apiClient.post('/carousel-batch/upload', payload);

      setResults(response.data);
    } catch (err) {
      console.error('Upload error:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to upload to Carousel 6';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const uploadCustomImage = async () => {
    if (gtinList.length === 0) {
      setError('Please enter and process GTINs first');
      return;
    }

    if (imageSource === 'file' && !imageFile) {
      setError('Please select an image file');
      return;
    }

    if (imageSource === 'url' && !imageUrl.trim()) {
      setError('Please enter an image URL');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('carousel_position', carouselPosition.toString());
      formData.append('gtins', gtinList.join('\n'));

      if (recipientGuid) {
        formData.append('recipient_guid', recipientGuid);
      }

      let response;

      if (imageSource === 'file') {
        formData.append('image', imageFile);
        response = await apiClient.post('/carousel-batch/upload-custom', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        formData.append('image_url', imageUrl);
        response = await apiClient.post('/carousel-batch/upload-custom-url', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      setResults(response.data);
    } catch (err) {
      console.error('Upload error:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to upload custom image';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Scan for matching images
  const scanForImages = async () => {
    // Parse GTINs directly from input (no need for separate process step)
    const gtins = gtinInput
      .split(/[\n,\s]+/)
      .map(g => g.trim())
      .filter(g => g.length > 0)
      .filter((value, index, self) => self.indexOf(value) === index);

    if (gtins.length === 0) {
      setError('Please enter at least one GTIN');
      return;
    }

    if (gtins.length > 500) {
      setError('Maximum 500 GTINs per scan. You have ' + gtins.length);
      return;
    }

    if (!oldImageFile) {
      setError('Please select the image to search for');
      return;
    }

    // Update gtinList for display purposes
    setGtinList(gtins);
    setScanning(true);
    setError('');
    setScanResults(null);
    setSelectedMatches({});

    try {
      const formData = new FormData();
      formData.append('gtins', gtins.join('\n'));
      formData.append('old_image', oldImageFile);
      formData.append('similarity_threshold', similarityThreshold.toString());

      const response = await apiClient.post('/carousel-batch/find-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setScanResults(response.data);
      // Select all matches by default
      const allSelected = {};
      (response.data.matches || []).forEach((_, index) => {
        allSelected[index] = true;
      });
      setSelectedMatches(allSelected);
    } catch (err) {
      console.error('Scan error:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to scan images';
      setError(errorMessage);
    } finally {
      setScanning(false);
    }
  };

  // Replace selected images
  const replaceSelectedImages = async () => {
    if (!newImageFile) {
      setError('Please select the new image to replace with');
      return;
    }

    const selectedItems = (scanResults?.matches || []).filter((_, index) => selectedMatches[index]);
    if (selectedItems.length === 0) {
      setError('Please select at least one item to replace');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('new_image', newImageFile);
      formData.append('matches_json', JSON.stringify(selectedItems.map(item => ({
        gtin: item.gtin,
        carousel_position: item.carousel_position,
        current_image_url: item.current_image_url,
        similarity_score: item.similarity_score
      }))));

      if (recipientGuid) {
        formData.append('recipient_guid', recipientGuid);
      }

      const response = await apiClient.post('/carousel-batch/replace-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setResults(response.data);
      // Clear scan results after successful replacement
      setScanResults(null);
      setSelectedMatches({});
      // Refresh history
      fetchReplacementHistory();
    } catch (err) {
      console.error('Replace error:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to replace images';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Toggle match selection
  const toggleMatchSelection = (index) => {
    setSelectedMatches(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Select/deselect all matches
  const toggleAllMatches = (selectAll) => {
    const newSelection = {};
    (scanResults?.matches || []).forEach((_, index) => {
      newSelection[index] = selectAll;
    });
    setSelectedMatches(newSelection);
  };

  const getSelectedCount = () => {
    return Object.values(selectedMatches).filter(Boolean).length;
  };

  const handleUpload = () => {
    if (uploadMode === 'packshots') {
      uploadToCarousel6();
    } else {
      uploadCustomImage();
    }
  };

  const canUpload = () => {
    if (gtinList.length === 0) return false;
    if (uploadMode === 'custom') {
      if (imageSource === 'file' && !imageFile) return false;
      if (imageSource === 'url' && !imageUrl.trim()) return false;
    }
    return true;
  };

  const getUploadButtonText = () => {
    const count = gtinList.length;
    if (loading) return 'Uploading...';
    if (uploadMode === 'packshots') {
      return `Upload ${count} GTINs to Carousel 6`;
    } else {
      return `Upload to Carousel ${carouselPosition} for ${count} GTINs`;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Batch Carousel Upload
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Upload images to carousel positions for multiple GTINs
          </Typography>
        </Box>

        {/* Mode Toggle */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Upload Mode
          </Typography>
          <ToggleButtonGroup
            value={uploadMode}
            exclusive
            onChange={handleModeChange}
            aria-label="upload mode"
          >
            <ToggleButton value="packshots" aria-label="pack shots mode">
              <ImageIcon sx={{ mr: 1 }} />
              Pack Shots
            </ToggleButton>
            <ToggleButton value="custom" aria-label="custom image mode">
              <CloudUploadIcon sx={{ mr: 1 }} />
              Custom Image
            </ToggleButton>
            <ToggleButton value="findreplace" aria-label="find and replace mode">
              <FindReplaceIcon sx={{ mr: 1 }} />
              Find & Replace
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          {uploadMode === 'packshots' ? (
            <Typography variant="body2">
              <strong>Pack Shots Mode:</strong> Automatically fetches the original product images from Syndigo Marketplace
              and uploads them to Carousel Position 6 (Detailed Product View 5).
            </Typography>
          ) : uploadMode === 'findreplace' ? (
            <Typography variant="body2">
              <strong>Find & Replace Mode:</strong> Search for products that have a specific image in their carousel and replace it with a new image.
              Uses perceptual hashing to find similar images even if they differ slightly. Run a dry scan first to preview matches.
            </Typography>
          ) : (
            <Typography variant="body2">
              <strong>Custom Image Mode:</strong> Upload your own image (file or URL) to any carousel position (1-6)
              for multiple GTINs at once.
            </Typography>
          )}
        </Alert>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {/* Custom Image Options (only in custom mode) */}
            {uploadMode === 'custom' && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Step 1: Select Image
                </Typography>

                {/* Image Source Toggle */}
                <Box sx={{ mb: 2 }}>
                  <ToggleButtonGroup
                    value={imageSource}
                    exclusive
                    onChange={handleImageSourceChange}
                    size="small"
                    aria-label="image source"
                  >
                    <ToggleButton value="file" aria-label="file upload">
                      <CloudUploadIcon sx={{ mr: 1 }} />
                      File Upload
                    </ToggleButton>
                    <ToggleButton value="url" aria-label="url input">
                      <LinkIcon sx={{ mr: 1 }} />
                      Image URL
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* File Upload */}
                {imageSource === 'file' && (
                  <Box sx={{ mb: 2 }}>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => fileInputRef.current?.click()}
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                    >
                      {imageFile ? imageFile.name : 'Choose Image File'}
                    </Button>
                  </Box>
                )}

                {/* URL Input */}
                {imageSource === 'url' && (
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Image URL"
                      placeholder="https://example.com/image.jpg"
                      value={imageUrl}
                      onChange={handleUrlChange}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={loadImageFromUrl}
                      disabled={!imageUrl.trim() || loadingPreview}
                      startIcon={loadingPreview ? <CircularProgress size={16} /> : <ImageIcon />}
                    >
                      {loadingPreview ? 'Loading...' : 'Load Preview'}
                    </Button>
                  </Box>
                )}

                {/* Image Preview */}
                {imagePreview && (
                  <Card sx={{ mb: 2, maxWidth: 300 }}>
                    <CardMedia
                      component="img"
                      image={imagePreview}
                      alt="Image preview"
                      sx={{ maxHeight: 200, objectFit: 'contain' }}
                    />
                  </Card>
                )}

                {/* Carousel Position */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Carousel Position</InputLabel>
                  <Select
                    value={carouselPosition}
                    label="Carousel Position"
                    onChange={(e) => setCarouselPosition(e.target.value)}
                  >
                    {CAROUSEL_POSITIONS.map((pos) => (
                      <MenuItem key={pos.value} value={pos.value}>
                        {pos.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider sx={{ my: 2 }} />
              </Box>
            )}

            {/* Find & Replace Mode - Full Width Layout */}
            {uploadMode === 'findreplace' && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Step 1: Configure Search
                </Typography>

                <Grid container spacing={2}>
                  {/* Old Image (to find) */}
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Image to Find
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <input
                        type="file"
                        accept="image/*"
                        ref={oldImageInputRef}
                        onChange={handleOldImageSelect}
                        style={{ display: 'none' }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => oldImageInputRef.current?.click()}
                        startIcon={<SearchIcon />}
                        fullWidth
                        color={oldImageFile ? 'success' : 'primary'}
                        size="small"
                      >
                        {oldImageFile ? 'Change' : 'Select'}
                      </Button>
                    </Box>
                    {oldImagePreview && (
                      <Card sx={{ maxWidth: 150 }}>
                        <CardMedia
                          component="img"
                          image={oldImagePreview}
                          alt="Old image preview"
                          sx={{ height: 100, objectFit: 'contain' }}
                        />
                      </Card>
                    )}
                  </Grid>

                  {/* Similarity Threshold */}
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Similarity: {similarityThreshold}
                    </Typography>
                    <Slider
                      value={similarityThreshold}
                      onChange={(e, val) => setSimilarityThreshold(val)}
                      min={0}
                      max={30}
                      step={1}
                      marks={[
                        { value: 0, label: 'Exact' },
                        { value: 15, label: 'Normal' },
                        { value: 30, label: 'Lenient' }
                      ]}
                      size="small"
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />
              </Box>
            )}

            <Typography variant="h6" gutterBottom>
              {uploadMode === 'custom' ? 'Step 2: Enter GTINs' : uploadMode === 'findreplace' ? 'Step 2: Enter GTINs' : 'Step 1: Enter GTINs'}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={uploadMode === 'custom' ? 6 : 10}
              variant="outlined"
              placeholder="Enter GTINs (one per line or comma-separated)&#10;&#10;Example:&#10;00011110148797&#10;00011110089663&#10;00011110036322"
              value={gtinInput}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Recipient GUID (Optional)"
                placeholder="Leave empty to use default"
                value={recipientGuid}
                onChange={handleRecipientChange}
                helperText="Optional: Specify a recipient GUID for the upload"
              />
            </Box>
            {/* Only show Process GTINs button for non-findreplace modes */}
            {uploadMode !== 'findreplace' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={processGtinInput}
                  disabled={!gtinInput.trim()}
                >
                  Process GTINs
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearAll}
                  startIcon={<ClearIcon />}
                >
                  Clear All
                </Button>
              </Box>
            )}
            {/* For findreplace mode, just show Clear button */}
            {uploadMode === 'findreplace' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={clearAll}
                  startIcon={<ClearIcon />}
                >
                  Clear All
                </Button>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {/* Standard modes: Review & Upload */}
            {uploadMode !== 'findreplace' && (
              <>
                <Typography variant="h6" gutterBottom>
                  {uploadMode === 'custom' ? 'Step 3: Review & Upload' : 'Step 2: Review & Upload'}
                </Typography>
                {gtinList.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>{gtinList.length} GTINs</strong> ready for upload
                      {uploadMode === 'custom' && (
                        <> to <strong>Carousel {carouselPosition}</strong></>
                      )}
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflowY: 'auto', mt: 1 }}>
                      {gtinList.map((gtin, index) => (
                        <Chip
                          key={index}
                          label={gtin}
                          size="small"
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleUpload}
                  disabled={loading || !canUpload()}
                  startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
                  sx={{ mb: 2 }}
                >
                  {getUploadButtonText()}
                </Button>
              </>
            )}

            {/* Find & Replace mode: Scan and Results */}
            {uploadMode === 'findreplace' && (
              <>
                <Typography variant="h6" gutterBottom>
                  Step 3: Scan & Replace
                </Typography>

                {/* Scan Button */}
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={scanForImages}
                  disabled={scanning || !gtinInput.trim() || !oldImageFile}
                  startIcon={scanning ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ mb: 2 }}
                >
                  {scanning ? 'Scanning...' : 'Scan for Matches'}
                </Button>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {/* Scan Results with Thumbnails */}
                {scanResults && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">
                        Found <strong>{scanResults.matches_found}</strong> matches
                      </Typography>
                      {scanResults.matches_found > 0 && (
                        <Box>
                          <Button size="small" onClick={() => toggleAllMatches(true)}>Select All</Button>
                          <Button size="small" onClick={() => toggleAllMatches(false)}>Deselect All</Button>
                        </Box>
                      )}
                    </Box>

                    {scanResults.matches_found === 0 ? (
                      <Alert severity="info">
                        No matching images found in the scanned GTINs.
                      </Alert>
                    ) : (
                      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        {scanResults.matches.map((match, index) => (
                          <Paper
                            key={index}
                            variant="outlined"
                            sx={{
                              p: 1,
                              mb: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              bgcolor: selectedMatches[index] ? 'action.selected' : 'background.paper'
                            }}
                          >
                            <Checkbox
                              checked={!!selectedMatches[index]}
                              onChange={() => toggleMatchSelection(index)}
                            />
                            {match.current_image_url && (
                              <Box
                                component="img"
                                src={match.current_image_url}
                                alt={`GTIN ${match.gtin}`}
                                sx={{ width: 60, height: 60, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1 }}
                              />
                            )}
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                {match.gtin}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {match.position_name} ({(match.similarity_score * 100).toFixed(0)}% match)
                              </Typography>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {/* New Image Selection and Replace Button */}
                    {scanResults.matches_found > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Replacement Image
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                          <input
                            type="file"
                            accept="image/*"
                            ref={newImageInputRef}
                            onChange={handleNewImageSelect}
                            style={{ display: 'none' }}
                          />
                          <Button
                            variant="outlined"
                            onClick={() => newImageInputRef.current?.click()}
                            startIcon={<CloudUploadIcon />}
                            color={newImageFile ? 'success' : 'primary'}
                            size="small"
                          >
                            {newImageFile ? newImageFile.name : 'Select New Image'}
                          </Button>
                          {newImagePreview && (
                            <Box
                              component="img"
                              src={newImagePreview}
                              alt="New image"
                              sx={{ width: 60, height: 60, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1 }}
                            />
                          )}
                        </Box>
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={replaceSelectedImages}
                          disabled={loading || !newImageFile || getSelectedCount() === 0}
                          startIcon={loading ? <CircularProgress size={20} /> : <FindReplaceIcon />}
                        >
                          {loading ? 'Replacing...' : `Replace ${getSelectedCount()} Selected Images`}
                        </Button>
                      </Box>
                    )}
                  </Paper>
                )}
              </>
            )}
          </Grid>
        </Grid>

        {results && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              Upload Results
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <Typography variant="h4">{results.total}</Typography>
                  <Typography variant="body2" color="text.secondary">Total</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                  <Typography variant="h4" color="success.main">{results.successful}</Typography>
                  <Typography variant="body2" color="text.secondary">Successful</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50' }}>
                  <Typography variant="h4" color="error.main">{results.failed}</Typography>
                  <Typography variant="body2" color="text.secondary">Failed</Typography>
                </Paper>
              </Grid>
            </Grid>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>GTIN</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.results.map((result, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {result.gtin}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`Carousel ${result.carousel_position}`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {result.success ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Success"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<ErrorIcon />}
                            label="Failed"
                            color="error"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {result.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {result.error && (
                          <Tooltip title={result.error}>
                            <Typography
                              variant="body2"
                              color="error"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 200
                              }}
                            >
                              {result.error}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Replacement History (only in findreplace mode) */}
        {uploadMode === 'findreplace' && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Replacement History {historyTotal > 0 && <Chip label={`${historyTotal} total`} size="small" sx={{ ml: 1 }} />}
              </Typography>
              <Button
                size="small"
                onClick={fetchReplacementHistory}
                disabled={loadingHistory}
                startIcon={loadingHistory ? <CircularProgress size={16} /> : null}
              >
                {loadingHistory ? 'Loading...' : 'Refresh'}
              </Button>
            </Box>

            {replacementHistory.length === 0 ? (
              <Alert severity="info">
                No replacement history yet. Replace some images to see them here.
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>GTIN</TableCell>
                      <TableCell>Position</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {replacementHistory.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {item.gtin}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.position_name}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {item.status === 'completed' ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Replaced"
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Tooltip title={item.error_message || 'Failed'}>
                              <Chip
                                icon={<ErrorIcon />}
                                label="Failed"
                                color="error"
                                size="small"
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.user_email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(item.created_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default CarouselBatch;
